// ── Central Role Permissions Utility ──────────────────────────────────────
// Based on Excel: User_Management_Templaterevised30042026.xlsx
// Usage: import { can, ROLE } from '../utils/permissions';

const user = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); }
  catch { return {}; }
};

export const getRole = () => user()?.role?.toUpperCase() || '';

// Permission map — exactly from Excel
const PERMISSIONS = {
  OWNER: [
    'GUEST_REG','REGISTRATION','ADMISSION','ROOM_ALLOCATION',
    'FINANCE_FULL','FINANCE_READ','ACCOUNTING',
    'MODIFY_RATE','MODIFY_FEE',
    'CREATE_PR','APPROVE_PR','CREATE_CR','APPROVE_CR',
    'RECEIVE_STOCK','ISSUE_STOCK',
    'APPLY_LEAVES','APPROVE_LEAVES',
    'CANTEEN_MENU_CREATE','CANTEEN_MENU_VIEW',
    'ATTENDANCE_VIEW',
    'NOTIFICATION_TEMPLATES','REPORTS',
    'LAUNDRY_MGMT',
    'ADD_DELETE_USER','ADD_ADMIN','ADMINISTRATION',
  ],
  SUPER_ADMIN: [
    'GUEST_REG','REGISTRATION','ADMISSION','ROOM_ALLOCATION',
    'FINANCE_FULL','FINANCE_READ','ACCOUNTING',
    'MODIFY_RATE','MODIFY_FEE',
    'CREATE_PR','APPROVE_PR','CREATE_CR','APPROVE_CR',
    'RECEIVE_STOCK','ISSUE_STOCK',
    'APPLY_LEAVES','APPROVE_LEAVES',
    'CANTEEN_MENU_CREATE','CANTEEN_MENU_VIEW',
    'ATTENDANCE_VIEW',
    'NOTIFICATION_TEMPLATES','REPORTS',
    'LAUNDRY_MGMT',
    'ADD_DELETE_USER','ADD_ADMIN','ADMINISTRATION',
  ],
  ADMIN: [
    // ✅ Full access — same as OWNER
    'GUEST_REG','REGISTRATION','ADMISSION','ROOM_ALLOCATION',
    'FINANCE_FULL','FINANCE_READ','ACCOUNTING',
    'MODIFY_RATE','MODIFY_FEE',
    'CREATE_PR','APPROVE_PR','CREATE_CR','APPROVE_CR',
    'RECEIVE_STOCK','ISSUE_STOCK',
    'APPLY_LEAVES','APPROVE_LEAVES',
    'CANTEEN_MENU_CREATE','CANTEEN_MENU_VIEW',
    'ATTENDANCE_VIEW',
    'NOTIFICATION_TEMPLATES','REPORTS',
    'LAUNDRY_MGMT',
    'ADD_DELETE_USER','ADD_ADMIN','ADMINISTRATION',
  ],
  PURCHASE_OFFICER: [
    // ✅ Full access — same as OWNER
    'GUEST_REG','REGISTRATION','ADMISSION','ROOM_ALLOCATION',
    'FINANCE_FULL','FINANCE_READ','ACCOUNTING',
    'MODIFY_RATE','MODIFY_FEE',
    'CREATE_PR','APPROVE_PR','CREATE_CR','APPROVE_CR',
    'RECEIVE_STOCK','ISSUE_STOCK',
    'APPLY_LEAVES','APPROVE_LEAVES',
    'CANTEEN_MENU_CREATE','CANTEEN_MENU_VIEW',
    'ATTENDANCE_VIEW',
    'NOTIFICATION_TEMPLATES','REPORTS',
    'LAUNDRY_MGMT',
    'ADD_DELETE_USER','ADD_ADMIN','ADMINISTRATION',
  ],
  CANTEEN_INCHARGE: [
    // ✅ Full Inventory access
    'CREATE_PR',           // Create Purchase Requisition
    'APPROVE_PR',          // Approve Purchase Requisition
    'CREATE_CR',           // Create Consumption Requisition
    'APPROVE_CR',          // Approve Consumption Requisition
    'RECEIVE_STOCK',       // Receive Stock
    'ISSUE_STOCK',         // Issue Stock
    // Canteen
    'CANTEEN_MENU_CREATE', // Full canteen access including plan/publish
    'CANTEEN_MENU_VIEW',   // View canteen menu
    // Finance
    'FINANCE_FULL',        // Finance module — full access
    'FINANCE_READ',        // Finance read access
    'ACCOUNTING',          // Outstanding dues, top-up, laundry, electricity
  ],
  LAUNDRY_INCHARGE: [
    'LAUNDRY_MGMT',
  ],
  FINANCE_MANAGER: [
    // ✅ Full access — except APPROVE_PR (button hidden in frontend)
    'GUEST_REG','REGISTRATION','ADMISSION','ROOM_ALLOCATION',
    'FINANCE_FULL','FINANCE_READ','ACCOUNTING',
    'MODIFY_RATE','MODIFY_FEE',
    'CREATE_PR','CREATE_CR','APPROVE_CR',
    // NO APPROVE_PR — Approve Purchase Requisition button hidden
    'RECEIVE_STOCK','ISSUE_STOCK',
    'APPLY_LEAVES','APPROVE_LEAVES',
    'CANTEEN_MENU_CREATE','CANTEEN_MENU_VIEW',
    'ATTENDANCE_VIEW',
    'NOTIFICATION_TEMPLATES','REPORTS',
    'LAUNDRY_MGMT',
    'ADD_DELETE_USER','ADD_ADMIN','ADMINISTRATION',
  ],
  HOUSEKEEPING: [
    'CREATE_PR','APPROVE_PR',
  ],
  STORE_INCHARGE: [
    'CREATE_PR',     // Create Purchase Requisition
    'CREATE_CR',     // Create Consumption Requisition
    'RECEIVE_STOCK', // Receive Stock
    'ISSUE_STOCK',   // Issue Stock
    // NO APPROVE_PR, NO APPROVE_CR
  ],
  STUDENT: [],
  PARENT:  [],
};

// Main check function — reads fresh from localStorage every call
export const can = (permission) => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const role = u?.role?.toUpperCase() || '';
    return !!(PERMISSIONS[role] || []).includes(permission);
  } catch {
    return false;
  }
};

// Sidebar visibility — show menu if user has ANY permission in that module
export const canSeeMenu = {
  dashboard:     () => can('REGISTRATION') || can('FINANCE_FULL') || can('ATTENDANCE_VIEW'),
  registration:  () => can('GUEST_REG') || can('REGISTRATION'),
  admission:     () => can('ADMISSION'),
  rooms:         () => can('ROOM_ALLOCATION') || can('FINANCE_FULL'),
  finance:       () => can('FINANCE_FULL') || can('FINANCE_READ') || can('ACCOUNTING'),
  canteen:       () => can('CANTEEN_MENU_VIEW') || can('CANTEEN_MENU_CREATE'),
  inventory:     () => can('CREATE_PR') || can('APPROVE_PR') || can('CREATE_CR') || can('APPROVE_CR') || can('RECEIVE_STOCK') || can('ISSUE_STOCK'),
  notifications: () => can('NOTIFICATION_TEMPLATES'),
  leaves:        () => can('APPLY_LEAVES') || can('APPROVE_LEAVES'),
  reports:       () => can('REPORTS'),
  laundry:       () => can('LAUNDRY_MGMT'),
  users:         () => can('ADD_DELETE_USER') || can('ADD_ADMIN') || can('ADMINISTRATION'),
};